import {
    afterRenderEffect,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    input,
    linkedSignal,
    signal,
    viewChild
} from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

/**
 * A log or error message that may be very long — a synchronization error is often a whole list of
 * rejected rows. Clamped to five lines, click to expand, copy button on hover.
 */
@Component({
    selector: 'app-expandable-message',
    templateUrl: './expandable-message.component.html',
    styleUrl: './expandable-message.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgbTooltip]
})
export class ExpandableMessageComponent {
    readonly text = input.required<string>();

    private readonly toastr = inject(ToastrService);
    private readonly body = viewChild.required<ElementRef<HTMLElement>>('body');

    /** A new message collapses again — the previous one's expansion says nothing about this one. */
    protected readonly expanded = linkedSignal<string, boolean>({
        source: () => this.text(),
        computation: () => false
    });

    /** Whether the clamp is actually hiding anything. Drives the affordance, never the clamp. */
    protected readonly overflowing = signal(false);

    constructor() {
        // Measured under a clamp that does not depend on the result, so this cannot feed itself.
        afterRenderEffect(() => {
            this.text();
            if (this.expanded()) return;

            const element = this.body().nativeElement;
            this.overflowing.set(element.scrollHeight - element.clientHeight > 1);
        });
    }

    protected toggle(): void {
        if (!this.overflowing() && !this.expanded()) return;
        this.expanded.update(expanded => !expanded);
    }

    protected async copy(event: Event): Promise<void> {
        event.stopPropagation();
        try {
            await navigator.clipboard.writeText(this.text());
            this.toastr.success('Besked kopieret.');
        } catch {
            this.toastr.error('Kunne ikke kopiere beskeden.');
        }
    }
}
